import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import * as types from '../mutation-types';
import { INBOX_TYPES } from 'shared/mixins/inboxMixin';
import InboxesAPI from '../../api/inboxes';
import WebChannel from '../../api/channel/webChannel';
import FBChannel from '../../api/channel/fbChannel';
import TwilioChannel from '../../api/channel/twilioChannel';
import InstagramChannel from '../../api/channel/instagramChannel';
import { throwErrorMessage } from '../utils/api';
import AnalyticsHelper from '../../helper/AnalyticsHelper';
import { ACCOUNT_EVENTS } from '../../helper/AnalyticsHelper/events';

const classifyWhatsAppError = errorData => {
  if (!errorData.code || !errorData.subcode) return null;

  const code = errorData.code;
  const subcode = errorData.subcode;

  if (code === 100) {
    switch (subcode) {
      case 2388124:
        return 'TEMPLATE_EDIT_RATE_LIMIT';
      case 2388105:
        return 'TEMPLATE_NAME_EXISTS';
      case 2388001:
        return 'TEMPLATE_FORMAT_INVALID';
      case 2388103:
        return 'TEMPLATE_CONTENT_INVALID';
      default:
        return 'TEMPLATE_VALIDATION_ERROR';
    }
  }

  return 'UNKNOWN_ERROR';
};

const extractErrorMessage = error => {
  if (error.response?.data?.error) {
    const errorData = error.response.data.error;

    if (typeof errorData === 'object' && errorData.message) {
      const classification = classifyWhatsAppError(errorData);
      return {
        message: errorData.message,
        classification: classification,
        code: errorData.code,
        subcode: errorData.subcode,
        details: errorData.details,
        user_action: errorData.user_action,
      };
    }

    if (typeof errorData === 'string') {
      return { message: errorData };
    }
  }

  return { message: error.message || 'An unexpected error occurred' };
};

const extractTemplatesFromRecord = record => {
  if (!record) {
    return [];
  }

  const topLevelTemplates = Array.isArray(record.message_templates)
    ? record.message_templates
    : [];
  const additionalAttributeTemplates = Array.isArray(
    record.additional_attributes?.message_templates
  )
    ? record.additional_attributes.message_templates
    : [];

  if (topLevelTemplates.length) {
    return topLevelTemplates;
  }

  if (additionalAttributeTemplates.length) {
    return additionalAttributeTemplates;
  }

  return [];
};

const applyTemplatesToRecord = (record, sourceRecord) => {
  if (sourceRecord?.message_templates?.length) {
    return {
      ...record,
      message_templates: sourceRecord.message_templates,
    };
  }

  if (sourceRecord?.additional_attributes?.message_templates?.length) {
    return {
      ...record,
      additional_attributes: {
        ...record.additional_attributes,
        message_templates: sourceRecord.additional_attributes.message_templates,
      },
    };
  }

  return record;
};

const mergeTemplatesFromExistingRecords = (existingRecords, newRecords) => {
  if (
    !Array.isArray(existingRecords) ||
    !existingRecords.length ||
    !Array.isArray(newRecords)
  ) {
    return newRecords;
  }

  const existingRecordsMap = existingRecords.reduce((acc, record) => {
    acc[record.id] = record;
    return acc;
  }, {});

  return newRecords.map(record => {
    const existingRecord = existingRecordsMap[record.id];
    if (!existingRecord) {
      return record;
    }

    const existingTemplates = extractTemplatesFromRecord(existingRecord);
    const incomingTemplates = extractTemplatesFromRecord(record);

    if (!existingTemplates.length || incomingTemplates.length) {
      return record;
    }

    return applyTemplatesToRecord(record, existingRecord);
  });
};

const buildInboxData = inboxParams => {
  const formData = new FormData();
  const { channel = {}, ...inboxProperties } = inboxParams;
  Object.keys(inboxProperties).forEach(key => {
    formData.append(key, inboxProperties[key]);
  });
  const { selectedFeatureFlags, ...channelParams } = channel;
  if (selectedFeatureFlags) {
    if (selectedFeatureFlags.length) {
      selectedFeatureFlags.forEach(featureFlag => {
        formData.append(`channel[selected_feature_flags][]`, featureFlag);
      });
    } else {
      formData.append('channel[selected_feature_flags][]', '');
    }
  }
  Object.keys(channelParams).forEach(key => {
    formData.append(`channel[${key}]`, channel[key]);
  });
  return formData;
};

export const state = {
  records: [],
  uiFlags: {
    isFetching: false,
    isFetchingItem: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    isUpdatingIMAP: false,
    isUpdatingSMTP: false,
  },
};

export const getters = {
  getInboxes($state) {
    return $state.records;
  },
  getWhatsAppTemplates: $state => inboxId => {
    const [inbox] = $state.records.filter(
      record => record.id === Number(inboxId)
    );

    const {
      message_templates: whatsAppMessageTemplates,
      additional_attributes: additionalAttributes,
    } = inbox || {};

    const { message_templates: apiInboxMessageTemplates } =
      additionalAttributes || {};
    const messagesTemplates =
      whatsAppMessageTemplates || apiInboxMessageTemplates;

    if (messagesTemplates instanceof Array) {
      if (inbox.channel_type === 'Channel::TwilioSms')
        return messagesTemplates.map(template => ({
          ...template,
          isTwilioTemplate: true,
        }));

      return messagesTemplates;
    }
    return [];
  },
  getNewConversationInboxes($state) {
    return $state.records.filter(inbox => {
      const { channel_type: channelType, phone_number: phoneNumber = '' } =
        inbox;

      const isEmailChannel = channelType === INBOX_TYPES.EMAIL;
      const isSmsChannel =
        channelType === INBOX_TYPES.TWILIO &&
        phoneNumber.startsWith('whatsapp');
      return isEmailChannel || isSmsChannel;
    });
  },
  getInbox: $state => inboxId => {
    const [inbox] = $state.records.filter(
      record => record.id === Number(inboxId)
    );
    return inbox || {};
  },
  getUIFlags($state) {
    return $state.uiFlags;
  },
  getEmailInboxes($state) {
    return $state.records.filter(
      item => item.channel_type === INBOX_TYPES.EMAIL
    );
  },
  getWebsiteInboxes($state) {
    return $state.records.filter(item => item.channel_type === INBOX_TYPES.WEB);
  },
  getTwilioInboxes($state) {
    return $state.records.filter(
      item => item.channel_type === INBOX_TYPES.TWILIO
    );
  },
  getSMSInboxes($state) {
    return $state.records.filter(
      item =>
        item.channel_type === INBOX_TYPES.SMS ||
        (item.channel_type === INBOX_TYPES.TWILIO && item.medium === 'sms')
    );
  },
  getWhatsappInboxes($state) {
    return $state.records.filter(
      item =>
        item.channel_type === INBOX_TYPES.WHATSAPP ||
        item.channel_type === INBOX_TYPES.TWILIO
    );
  },
  dialogFlowEnabledInboxes($state) {
    return $state.records.filter(
      item => item.channel_type !== INBOX_TYPES.EMAIL
    );
  },
};

const sendAnalyticsEvent = channelType => {
  AnalyticsHelper.track(ACCOUNT_EVENTS.ADDED_AN_INBOX, {
    channelType,
  });
};

export const actions = {
  revalidate: async ({ commit }, { newKey }) => {
    try {
      const isExistingKeyValid = await InboxesAPI.validateCacheKey(newKey);
      if (!isExistingKeyValid) {
        const response = await InboxesAPI.refetchAndCommit(newKey);
        commit(types.default.SET_INBOXES, response.data.payload);
      }
    } catch (error) {
      // Ignore error
    }
  },
  get: async ({ commit }) => {
    commit(types.default.SET_INBOXES_UI_FLAG, { isFetching: true });
    try {
      const response = await InboxesAPI.get(true);
      commit(types.default.SET_INBOXES_UI_FLAG, { isFetching: false });
      commit(types.default.SET_INBOXES, response.data.payload);
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, { isFetching: false });
    }
  },
  getWithTemplates: async ({ commit }, inboxId) => {
    const response = await InboxesAPI.getWithTemplates(inboxId);
    commit(types.default.SET_INBOXES_ITEM, response.data);
    return response;
  },
  createChannel: async ({ commit }, params) => {
    try {
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: true });
      const response = await WebChannel.create(params);
      commit(types.default.ADD_INBOXES, response.data);
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: false });
      const { channel = {} } = params;
      sendAnalyticsEvent(channel.type);
      return response.data;
    } catch (error) {
      const errorMessage = error?.response?.data?.message;
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: false });
      throw new Error(errorMessage);
    }
  },
  createWebsiteChannel: async ({ commit }, params) => {
    try {
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: true });
      const response = await WebChannel.create(buildInboxData(params));
      commit(types.default.ADD_INBOXES, response.data);
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: false });
      sendAnalyticsEvent('website');
      return response.data;
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: false });
      return throwErrorMessage(error);
    }
  },
  createTwilioChannel: async ({ commit }, params) => {
    try {
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: true });
      const response = await TwilioChannel.create(params);
      commit(types.default.ADD_INBOXES, response.data);
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: false });
      sendAnalyticsEvent('twilio');
      return response.data;
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: false });
      throw error;
    }
  },
  createFBChannel: async ({ commit }, params) => {
    try {
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: true });
      const response = await FBChannel.create(params);
      commit(types.default.ADD_INBOXES, response.data);
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: false });
      sendAnalyticsEvent('facebook');
      return response.data;
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: false });
      throw new Error(error);
    }
  },
  createInstagramChannel: async ({ commit }, params) => {
    try {
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: true });
      const response = await InstagramChannel.create(params);
      commit(types.default.ADD_INBOXES, response.data);
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: false });
      sendAnalyticsEvent('instagram');
      return response.data;
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: false });
      throw new Error(error);
    }
  },
  updateInbox: async ({ commit }, { id, formData = true, ...inboxParams }) => {
    commit(types.default.SET_INBOXES_UI_FLAG, { isUpdating: true });
    try {
      const response = await InboxesAPI.update(
        id,
        formData ? buildInboxData(inboxParams) : inboxParams
      );
      commit(types.default.EDIT_INBOXES, response.data);
      commit(types.default.SET_INBOXES_UI_FLAG, { isUpdating: false });
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, { isUpdating: false });
      throwErrorMessage(error);
    }
  },
  updateInboxIMAP: async ({ commit }, { id, ...inboxParams }) => {
    commit(types.default.SET_INBOXES_UI_FLAG, { isUpdatingIMAP: true });
    try {
      const response = await InboxesAPI.update(id, inboxParams);
      commit(types.default.EDIT_INBOXES, response.data);
      commit(types.default.SET_INBOXES_UI_FLAG, { isUpdatingIMAP: false });
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, { isUpdatingIMAP: false });
      throwErrorMessage(error);
    }
  },
  updateInboxSMTP: async ({ commit }, { id, ...inboxParams }) => {
    commit(types.default.SET_INBOXES_UI_FLAG, { isUpdatingSMTP: true });
    try {
      const response = await InboxesAPI.update(id, inboxParams);
      commit(types.default.EDIT_INBOXES, response.data);
      commit(types.default.SET_INBOXES_UI_FLAG, { isUpdatingSMTP: false });
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, { isUpdatingSMTP: false });
      throwErrorMessage(error);
    }
  },
  delete: async ({ commit }, inboxId) => {
    commit(types.default.SET_INBOXES_UI_FLAG, { isDeleting: true });
    try {
      await InboxesAPI.delete(inboxId);
      commit(types.default.DELETE_INBOXES, inboxId);
      commit(types.default.SET_INBOXES_UI_FLAG, { isDeleting: false });
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, { isDeleting: false });

      if (error?.response?.data?.code === 'INBOX_CONTINUITY_CONNECTED')
        throw error?.response?.data?.inboxes_names?.toString();
      throw new Error(error);
    }
  },
  reauthorizeFacebookPage: async ({ commit }, params) => {
    try {
      const response = await FBChannel.reauthorizeFacebookPage(params);
      commit(types.default.EDIT_INBOXES, response.data);
    } catch (error) {
      throw new Error(error.message);
    }
  },
  deleteInboxAvatar: async (_, inboxId) => {
    try {
      await InboxesAPI.deleteInboxAvatar(inboxId);
    } catch (error) {
      throw new Error(error);
    }
  },
  syncWhatsAppTemplates: async ({ commit }, inboxId) => {
    let response;
    let syncError;
    try {
      response = await InboxesAPI.syncWhatsAppTemplates(inboxId);
    } catch (error) {
      syncError = error;
    }

    try {
      const inboxResponse = await InboxesAPI.getWithTemplates(inboxId);
      commit(types.default.SET_INBOXES_ITEM, inboxResponse.data);
    } catch (error) {
      // Silently fail template fetch - don't block the response
      // Store update is best-effort; sync error takes precedence
    }

    if (syncError) {
      throw syncError;
    }
    return response.data;
  },
  createWhatsAppTemplate: async (
    { commit },
    { inboxId, templateData, isFormData = false }
  ) => {
    try {
      const response = await InboxesAPI.createWhatsAppTemplate(
        inboxId,
        templateData,
        isFormData
      );
      const inboxResponse = await InboxesAPI.show(inboxId);
      commit(types.default.EDIT_INBOXES, inboxResponse.data);
      return response.data;
    } catch (error) {
      const errorInfo = extractErrorMessage(error);
      const classification = classifyWhatsAppError(errorInfo);

      // Create a structured error object with classification for proper handling
      const structuredError = {
        ...errorInfo,
        classification: classification,
      };

      throw structuredError;
    }
  },
  updateWhatsAppTemplate: async (
    { commit },
    { inboxId, templateId, templateData, isFormData = false }
  ) => {
    try {
      const response = await InboxesAPI.updateWhatsAppTemplate(
        inboxId,
        templateId,
        templateData,
        isFormData
      );
      const inboxResponse = await InboxesAPI.show(inboxId);
      commit(types.default.EDIT_INBOXES, inboxResponse.data);
      return response.data;
    } catch (error) {
      const errorInfo = extractErrorMessage(error);
      const classification = classifyWhatsAppError(errorInfo);

      const structuredError = {
        ...errorInfo,
        classification: classification,
      };

      throw structuredError;
    }
  },
  deleteWhatsAppTemplate: async ({ commit }, { inboxId, template }) => {
    try {
      const response = await InboxesAPI.deleteWhatsAppTemplate(
        inboxId,
        template
      );
      const inboxResponse = await InboxesAPI.show(inboxId);
      commit(types.default.EDIT_INBOXES, inboxResponse.data);
      return response.data;
    } catch (error) {
      const errorInfo = extractErrorMessage(error);
      const classification = classifyWhatsAppError(errorInfo);

      const structuredError = {
        ...errorInfo,
        classification: classification,
      };

      throw structuredError;
    }
  },
  retryWhatsappConnection: async (_, { inboxId }) => {
    try {
      const response = await InboxesAPI.retryWhatsappConnection(inboxId);
      return response.data;
    } catch (error) {
      throw new Error(error);
    }
  },
  resubscribeInstagram: async (_, { inboxId }) => {
    try {
      const response = await InboxesAPI.resubscribeInstagram(inboxId);
      return response.data;
    } catch (error) {
      throw new Error(error);
    }
  },
};

const setInboxesRecords = ($state, data) => {
  $state.records = mergeTemplatesFromExistingRecords($state.records, data);
};

export const mutations = {
  [types.default.SET_INBOXES_UI_FLAG]($state, uiFlag) {
    $state.uiFlags = { ...$state.uiFlags, ...uiFlag };
  },
  [types.default.SET_INBOXES]: setInboxesRecords,
  [types.default.SET_INBOXES_ITEM]: MutationHelpers.setSingleRecord,
  [types.default.ADD_INBOXES]($state, data) {
    const exists = $state.records.some(record => record.id === data.id);
    if (!exists) {
      $state.records.push(data);
    }
  },
  [types.default.EDIT_INBOXES]: MutationHelpers.update,
  [types.default.DELETE_INBOXES]: MutationHelpers.destroy,
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
